import { useEffect, useMemo, useState } from "react";
import LoyaltySliderSection from "../../components/loyalty/LoyaltySliderSection";
import { CustomModal } from "../../components/ui";
import { AppleStoreLogo, ArrowLeftOutline, ArrowsLeftRight, GiftIcon, GooglePlayStoreLogo, LeftArrow, ShieldCheckIcon, ShieldStarIcon, StarsIcon, UserLineIcon } from "../../icons";
import usePersonStore from "../../store/personStore";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { LoyaltyProfileCardSkeleton } from "../../components/elements/skeletions/skeletions";
import Loader from "../../components/elements/loader";
import { useLoyaltyInfo } from "../../hooks/react-query/useLoyalty";

export const LoyaltyPage = () => {

  const { refetch } = useLoyaltyInfo({ enabled: false });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const loyaltyInfo = usePersonStore((store) => store.loyaltyInfo);

  const [openModal, setOpenModal] = useState(false)
  const [selectedTxs, setSelectedTxs] = useState(null)
  const [showAllTxs, setShowAllTxs] = useState(false)

  const heroImage = "/avis/images/loyalty-hand-car.png";
  const dottedLine = "/avis/images/loyalty-dotted-line.png";
  const redShape = "/avis/images/loyalty-red-shape.png";
  const loyaltyLogo = "/avis/images/loyalty-logo.svg";
  
  const loyaltyTier = useMemo(()=>{return loyaltyInfo?.loyaltyTier},[loyaltyInfo?.loyaltyTier])
  const navigate = useNavigate();

  const tierGradients = () => {
    switch (loyaltyTier) {
      case "Bronze" :
        return "bg-[linear-gradient(95.46deg,_#B87333_2.8%,_#D08A45_21.86%,_#BE7231_38.72%,_#D08A45_57.67%,_#BE7231_70.65%,_#D08A45_87.39%,_#D08A45_104.89%)]"
      case "Silver":
        return "bg-[linear-gradient(95.46deg,_#717171_2.8%,_#9E9898_21.86%,_#B5B0B0_38.72%,_#9E9898_57.67%,_#B5B0B0_70.65%,_#9E9898_87.39%,_#9E9898_104.89%)]"
      case "Gold":
        return "bg-[linear-gradient(95.46deg,_#CFAE4A_2.8%,_#E6C76A_21.86%,_#D1AD4C_38.72%,_#E6C76A_57.67%,_#D1AD4C_70.65%,_#E6C76A_87.39%,_#E6C76A_104.89%)]"
      default:
        return ""
    }
  }

  const current = useMemo(()=> { return Number(loyaltyInfo?.tierPoints ?? 0)},[loyaltyInfo]);
  const maxUpgradeAmount = useMemo(()=> {
    switch (loyaltyTier) {
      case "Bronze":
        return 7000;
      case "Silver":
        return 12000;
      default: 
        return 0
    }
  }, [loyaltyTier]);

    const maxRetainAmount = useMemo(()=> {
    switch (loyaltyTier) {
      case "Silver":
        return 6000;
      case "Gold":
        return 11000;
      default: 
        return 0
    }
  }, [loyaltyTier]);
  
  const progressRetainPct = useMemo(()=> {
    return Math.min(100, Math.max(0, (current / maxRetainAmount) * 100));
  },[current, maxRetainAmount])

  const progressUpgradePct = useMemo(()=> {
    return Math.min(100, Math.max(0, (current / maxUpgradeAmount) * 100));
  },[current, maxUpgradeAmount])

  
  const currentTierbg = {
    'Bronze': "/avis/images/loyalty-current-tier-bronze.png",
    'Silver': "/avis/images/loyalty-current-tier-silver.png",
    'Gold': "/avis/images/loyalty-current-tier-gold.png",
  }

  const tiersCards = [
    {
      title:"Bronze",
      subtitle: "Για να αναβαθμιστείς στη Silver, συγκέντρωσε 7.000 χλμ.",
      bgUrl: "/avis/images/loyalty-tier-bronze.png",
    },
    {
      title:"Silver",
      subtitle: "Για να αναβαθμιστείς στη Gold, συγκέντρωσε 12.000 χλμ.",
      bgUrl: "/avis/images/loyalty-tier-silver.png",
    },
    {
      title:"Gold",
      subtitle: "Για να διατηρήσεις τη Gold, συγκέντρωσε 11.000 χλμ.",
      bgUrl: "/avis/images/loyalty-tier-gold.png",
    },
  ]

  const getTxDateMs = (t) => {
    const d = t?.transactionDate ?? t?.processedDate;
    const ms = d ? Date.parse(d) : -Infinity;
    return Number.isNaN(ms) ? -Infinity : ms;
  };

  const transactions = (loyaltyInfo?.listOfLoyaltyTransactions ?? [])
    .slice()
    .sort((a, b) => getTxDateMs(b) - getTxDateMs(a));

  const txsToRender = showAllTxs ? transactions : transactions.slice(0, 2);

  const handleOpenTxsModal = (transaction) => {
    setSelectedTxs(transaction)
    setOpenModal(true)
  }

  return (
    <div className='max-h-[calc(100vh-60px)] lg:max-h-[calc(100vh-72px)]'>
      {!loyaltyTier && 
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-[2px]">
          <Loader />
        </div>
      }
      <section className="relative overflow-hidden">
        <div className="relative h-[360px] md:h-[400px]">
          <img
            src={heroImage}
            alt="Avis Loyalty"
            className="absolute inset-0 h-full w-full object-cover object-[center_100%]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(226,235,249,0.45)_0%,rgba(238,243,251,0.10)_40%,rgba(255,255,255,0)_60%)]" />

          <div className="absolute inset-x-0 top-0 z-[2] h-[93%] md:h-[93%]">
            <img
              src={redShape}
              alt=""
              aria-hidden="true"
              className="absolute -left-40 -top-5 inset-0 h-full w-full object-fill"
            />

            <img
              src={dottedLine}
              alt=""
              aria-hidden="true"
              className="absolute left-[20px] -top-14 md:h-[1910px] w-full object-fill"
            />

            <div className="flex flex-row gap-7 items-center relative z-[3] top-[86px] left-[calc(50%+100px)] -translate-x-1/2 -translate-y-1/2">
              <button onClick={(()=> {navigate("/dashboard");})}>
                <ArrowLeftOutline className='w-16 h-16' stroke={'white'}/>
              </button>
              <div className='flex flex-col gap-3 items-start'>
                <h5 className='text-white font-medium'>Καλώς ήρθες στο</h5>
                <img
                  src={loyaltyLogo}
                  alt=""
                  aria-hidden="true"
                  className="h-[48px] w-[257px]"
                  />
              </div> 
              {/* <h1 className="max-w-[865px] text-white">
                <span
                  className="block text-[18px] font-medium leading-[22px] tracking-[0.4px]"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Καλώς ήρθες στο
                </span>
                <span
                  className="block text-[40px] font-bold leading-[44px] tracking-[-0.7px]"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Avis More
                </span>
              </h1> */}
            </div>
          </div>

          <div className="absolute bottom-5 left-4 right-4 z-10 md:bottom-6 md:left-6 md:right-6">
            <div className="flex gap-3 overflow-x-auto pb-1">{/* cards */}</div>
          </div>
        </div>
      </section>

      <div className={`min-w-full max-w-[1200px] flex flex-col gap-6 pb-10 ${loyaltyTier === "silver" ? "px-5" : "px-48"} max-lg:px-5`} >

        {/* tier row boxes */}
        <div className={`relative z-[3] -mt-[90px] grid gap-6 ${loyaltyTier === "Silver" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>

          {/* profile info box */}
          <div className="flex flex-col gap-3 overflow-hidden rounded-[12px] border border-[#E2E2E2] bg-white shadow-[0_4px_8px_0_rgba(99,99,99,0.15)] p-4">
            <div className='flex flex-row w-full justify-between items-center'>
              <UserLineIcon className={'w-[25px] h-[25px]'}/>
              <div className={`inline-flex items-center h-[34px] w-[74px] justify-center rounded-md px-4 py-1 text-sm font-semibold text-white ${tierGradients()}`}>
                {loyaltyTier}
              </div>
            </div>
            
            <div className="pt-1 flex flex-col gap-1 text-[#595454]">
              {!!loyaltyInfo?.contactFirstName ? <p className="text-xl font-bold text-black">
                {loyaltyInfo?.contactFirstName} {loyaltyInfo?.contactLastName}
              </p> : <LoyaltyProfileCardSkeleton numberOfRows={1}/>}
              {!!loyaltyInfo?.memberCardNum ? <p className="text-base text-black">ID: {loyaltyInfo?.memberCardNum}</p> : <LoyaltyProfileCardSkeleton numberOfRows={1}/>}
              {!!loyaltyInfo?.joinedDate ? <p className="text-base text-black">Μέλος από: {new Date(loyaltyInfo?.joinedDate).toLocaleDateString('el-GR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}</p> : <LoyaltyProfileCardSkeleton numberOfRows={1}/>}
            </div>
          </div>

          {/* Next tier box */}
          {loyaltyTier !== "Gold" && <div className="flex flex-col gap-3 overflow-hidden rounded-[12px] border border-[#E2E2E2] bg-white shadow-[0_4px_8px_0_rgba(99,99,99,0.15)] p-4">
            <div className='flex flex-row w-full justify-between items-center'>
              <ShieldStarIcon className={'w-[25px] h-[25px]'}/>
              <div className={`inline-flex items-center h-[34px] w-[74px] justify-center rounded-md px-4 py-1 text-sm font-semibold text-white ${tierGradients()}`}>
                {loyaltyTier}
              </div>
            </div>
            {!!loyaltyTier ? <div>
              <span className="text-xl text-black font-bold ">
                {(maxUpgradeAmount - loyaltyInfo?.tierPoints).toLocaleString("el-GR") } χλμ
              </span>{" "}
              <span className="text-sm text-black">
                έως {new Date(new Date(loyaltyInfo?.joinedDate).setFullYear(new Date(loyaltyInfo?.joinedDate).getFullYear() + 1 )).toLocaleDateString('el-GR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </span>
              <p className="text-base text-black pt-2">για την επόμενη βαθμίδα</p>
              {/* <div className="mt-3 h-2 rounded-full bg-[#E2E2E2]" /> */}
              <div className="mt-3 h-2 w-full rounded-full bg-[#E2E2E2] overflow-hidden">
                <div
                  className={`h-full ${tierGradients()}`}
                  style={{ width: `${progressUpgradePct}%` }}
                />
              </div>
            </div> : <LoyaltyProfileCardSkeleton numberOfRows={3}/>}
          </div>}

          {/* Stay in-tier box */}
         {!!loyaltyTier && loyaltyTier !== "Bronze" && <div className="flex flex-col gap-3 overflow-hidden rounded-[12px] border border-[#E2E2E2] bg-white shadow-[0_4px_8px_0_rgba(99,99,99,0.15)] p-4">
            <div className='flex flex-row w-full justify-between items-center'>
              <ShieldCheckIcon className={'w-[25px] h-[25px]'}/>
              <div className={`inline-flex items-center h-[34px] w-[74px] justify-center rounded-md px-4 py-1 text-sm font-semibold text-white ${tierGradients()}`}>
                {loyaltyTier}
              </div>
            </div>
            <div>
              <span className="text-xl text-black font-bold ">
                {(maxRetainAmount - loyaltyInfo?.tierPoints).toLocaleString("el-GR")} χλμ
              </span>{" "}
              <span className="text-sm text-black">
                έως {new Date(new Date(loyaltyInfo?.joinedDate).setFullYear(new Date(loyaltyInfo?.joinedDate).getFullYear() + 1 )).toLocaleDateString('el-GR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </span>
              <p className="text-base text-black pt-2">για διατήρηση της βαθμίδας</p>
              <div className="mt-3 h-2 w-full rounded-full bg-[#E2E2E2] overflow-hidden">
                <div
                  className={`h-full ${tierGradients()}`}
                  style={{ width: `${progressRetainPct}%` }}
                />
              </div>
            </div>
          </div>}

          {/* Current award points box */}
          <div className="flex flex-col gap-3 overflow-hidden rounded-[12px] border border-[#E2E2E2] bg-white shadow-[0_4px_8px_0_rgba(99,99,99,0.15)] p-4">
            <div className='flex flex-row w-full justify-between items-center'>
              <GiftIcon className={'w-[25px] h-[25px]'}/>
              <div className={`inline-flex items-center h-[34px] w-[74px] justify-center rounded-md px-4 py-1 text-sm font-semibold text-white ${tierGradients()}`}>
                {loyaltyTier}
              </div>
            </div>
            <div className="pt-1 flex flex-col gap-1 text-[#595454]">
              <p className="text-xl font-bold text-black">
                {(loyaltyInfo?.awardPoints ?? "").toLocaleString("el-GR")}
              </p>
              <p className="text-base text-black">Διαθέσιμα χιλιόμετρα εξαργύρωσης</p>
            </div>
          </div>
        </div>

        {/* profile content */}
        <div
          id="sm-container"
          className="mx-auto w-full max-w-[880px] flex flex-col gap-10"
        >
          {/* Current miles to spend section */}
          <div className="flex flex-col rounded-[16px] py-6 bg-[#FFFFFF]">
            <div className='flex flex-col gap-4 px-6'>
              <div className="flex items-center rounded-[20px] bg-cover bg-center px-5 py-4" 
                style={{ backgroundImage: `url(${currentTierbg[loyaltyTier]})` }}
              >
                <div className="flex flex-row items-end gap-3 text-white">
                  <span className="font-semibold text-[40px]">
                    {(loyaltyInfo?.awardPoints ?? "").toLocaleString("el-GR")}
                  </span>
                  <span className="font-bold text-[28px] leading-loose">
                    διαθέσιμα χιλιόμετρα εξαργύρωσης
                  </span>
                </div>
              </div>
              <p className="text-[20px] font-bold text-black">
                Χιλιόμετρα εξαργύρωσης
              </p>
              <p className="text-[18px] leading-5 text-black">
                Κάθε χιλιόμετρο σε φέρνει πιο κοντά σε περισσότερα οφέλη.<br />Και όσο περισσότερο μας επιλέγεις, τόσο περισσότερα απολαμβάνεις.
              </p>
            </div>
            <img
              src={"/avis/images/loyalty-accrual-car.png"}
              alt=""
              aria-hidden="true"
              className="w-full -mt-7"
            />
            <div className='w-full flex justify-end pr-6 -mt-10'>
              <button 
                  className="rounded-[6px] border border-[#A5A5A5] bg-white px-10 py-3 text-[14px] font-semibold text-black hover:bg-fadedgrey/10"
                  // onClick={async ()=> {window.open(`${process.env.REACT_APP_CMS_BASE_URL}/el/avis-more-loyalty-program#pos-exargyrono-xiliometra`, "_blank", "noopener,noreferrer");}}
                  onClick={async ()=> {window.open(`https://myavis.gr/el/avis-more-loyalty-program#pos-exargyrono-xiliometra`, "_blank", "noopener,noreferrer");}}
                  >
                Ανακάλυψε πώς εξαργυρώνεις
              </button>
            </div>
          </div>

          {/* Next stop avis more section */}
          <div className="rounded-[18px] bg-[#FFFFFF] p-6">
            <div className='flex flex-col gap-4'>
              <h4 className="text-[20px] font-bold leading-[24px] tracking-[0] text-[#151F25]">
                Η επόμενη στάση στο Avis More
              </h4>
              <p className="text-[18px] leading-5 text-black">
                Το πρόγραμμα περιλαμβάνει τρεις βαθμίδες: Bronze, Silver και Gold.<br />Καθεμία με τα δικά της προνόμια.
              </p>
            </div>
            <div className="md:grid gap-4 md:grid-cols-3 md:justify-items-start">
              {tiersCards?.map((tier, index) => {
                return (<div 
                  key={tier.title} 
                  className="my-[26px] w-full rounded-[12px] px-4 py-5 flex flex-col gap-3 items-start justify-center min-h-[110px] bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${tier.bgUrl})` }}
                  >
                  <div className='flex flex-row w-full justify-between'>
                    <p className='text-[20px] font-bold text-white'>
                      {tier?.title}
                    </p>
                    <StarsIcon />
                  </div>
                  <p className='text-[14px] font-bold text-white'>
                    {tier?.subtitle}
                  </p>
                </div>)
              })}
            </div>

            <div className="w-full flex justify-end">
              <button 
                  className="rounded-[6px] border border-[#A5A5A5] bg-white px-10 py-3 text-[14px] font-semibold text-black hover:bg-fadedgrey/10"
                  // onClick={async ()=> {window.open(`${process.env.REACT_APP_CMS_BASE_URL}/el/avis-more-loyalty-program#pos-leitourgei`, "_blank", "noopener,noreferrer");}}
                  onClick={async ()=> {window.open(`https://myavis.gr/el/avis-more-loyalty-program#pos-leitourgei`, "_blank", "noopener,noreferrer");}}
                  >
                Μάθε πώς ανεβαίνεις βαθμίδα
              </button>
            </div>
          </div>

          {/* Upgrade miles section */}
          <div className="flex flex-col gap-[26px] rounded-[18px] bg-[#FFFFFF] p-6">
            <div className='flex flex-col gap-4'>
              <h4 className="text-[20px] font-bold leading-[24px] tracking-[0] text-[#151F25]">
                Χιλιόμετρα αναβάθμισης
              </h4>
              <p className="text-[18px] leading-5 text-black">
                Τα χιλιόμετρα αναβάθμισης σε ανεβάζουν βαθμίδα και ξεκλειδώνουν περισσότερα προνόμια.
              </p>
            </div>

            <div 
              className={"w-full min-h-fit rounded-[12px] bg-midgrey h-[284px] flex justify-end items-end p-4"}
              style={{ 
                backgroundImage: `url(/avis/images/loyalty-miles-upgrade-section.png)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat"
              }}
              >
              </div>
              <div className="w-full flex justify-end">
                <button 
                  className="rounded-[6px] border border-[#A5A5A5] bg-white px-10 py-3 text-[14px] font-semibold text-black hover:bg-white/90"
                  onClick={async ()=> {window.open(`https://myavis.gr/el/avis-more-loyalty-program#tiers-and-benefits`, "_blank", "noopener,noreferrer");}}
                  // onClick={async ()=> {window.open(`${process.env.REACT_APP_CMS_BASE_URL}/el/avis-more-loyalty-program#tiers-and-benefits`, "_blank", "noopener,noreferrer");}}
                  >
                  Δες τα προνόμιά σου
                </button>
              </div>
          </div>

          {/* How to win slider section */}
          <LoyaltySliderSection />

          {/* Mobile app download section */}
          <div className="flex flex-row max-md:flex-col max-md:gap-4 justify-between items-center rounded-[18px] bg-[#FFFFFF] p-6">
            <div className='flex flex-col gap-[6px]'>
              <h4 className="text-[20px] font-bold leading-[24px] tracking-[0] text-[#151F25]">
                Κατέβασε τώρα<br />την εφαρμογή
              </h4>
            </div>
            <img
              src={"/avis/images/apps-section-cars.png"}
              alt=""
              aria-hidden="true"
              className="w-[307px]"
            />
            <div className='flex flex-col max-md:flex-row max-md:items-center gap-1'>
              <button onClick={()=>{window.open("https://apps.apple.com/app/id6755685588", "_blank", "noopener,noreferrer")}}>
                <AppleStoreLogo className='h-[53px] w-[160px] max-md:h-[50px]' />
              </button>
              <button onClick={()=>{window.open("https://play.google.com/store/apps/details?id=gr.avis.myavismobileapp", "_blank", "noopener,noreferrer")}}>
                <GooglePlayStoreLogo className='h-[47px] w-[160px]' />
              </button>
            </div>
          </div>

          {/* Transaction history section */}
          <div className="flex flex-col gap-8 justify-between rounded-[18px] bg-[#FFFFFF] p-6">
            <h4 className="text-[20px] font-bold text-[#151F25]">
              Τελευταίες κινήσεις
            </h4>
            <div className={clsx( "flex flex-col gap-2", showAllTxs && transactions?.length > 2 && "max-h-[300px] overflow-y-auto scrollbar pr-1" )} >
              {transactions?.length === 0 
                ? <>
                  <p>Κάνε την πρώτη σου συναλλαγή για να κερδίσεις ακόμα περισσότερους πόντους.</p>
                  </>
                : txsToRender?.map((txs)=> {
                  return <button 
                    onClick={()=>{
                      handleOpenTxsModal(txs)
                    }}
                    className='hover:cursor-pointer w-full flex flex-row justify-between items-center p-3 rounded-xl border-[1px] border-mildgrey shadow-[0_2px_8px_rgba(0,0,0,0.12)]'>
                    <div className='flex flex-row gap-4 items-center'>
                      <ArrowsLeftRight className={`w-5 h-5 ${txs?.loyaltyTransactionType === "Accrual" ? "fill-[#047857]" : txs?.loyaltyTransactionType === "Redemption" ? "fill-[#B91C1C]" : "fill-[#3a3a3a]"} `}/> 
                      <div className='flex flex-col gap-1 items-start'>
                        <p className='text-sm text-black'>{txs?.loyaltyProduct}</p>
                        <p className='text-xs text-[#2c2c2c]'>{new Date(txs?.processedDate).toLocaleDateString('el-GR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}</p>
                      </div>
                    </div>
                    <p className='text-base font-bold text-[#2c2c2c]'>{txs?.awardPoints > 0 ? txs?.loyaltyTransactionType === "Accrual" ? `+${(txs?.awardPoints ?? "")?.toLocaleString("el-GR")}` : txs?.loyaltyTransactionType === "Redemption" ? `-${(txs?.awardPoints ?? "")?.toLocaleString("el-GR")}` : "" : txs?.tierPoints > 0 ? `+${(txs?.tierPoints ?? "")?.toLocaleString("el-GR")}` : (txs?.tierPoints ?? "")?.toLocaleString("el-GR")}</p>
                  </button>
                })}
            </div>
            {transactions?.length > 2 && <div className='w-full flex justify-end'>
              <button 
                  className="rounded-[6px] border border-[#A5A5A5] bg-white px-10 py-3 text-[14px] font-semibold text-black hover:bg-fadedgrey/10"
                  onClick={()=>{setShowAllTxs(!showAllTxs)}}
                  >
                {showAllTxs ? "Λιγότερες" : "Όλες οι κινήσεις"}
              </button>
            </div>}
            <CustomModal
              wrapperClass="grid"
              handleClick={(value) => setOpenModal(value)}
              openModal={openModal}
            >
              <div className="w-full mx-auto py-10 flex flex-col items-center gap-y-5">
                  <div className="flex gap-3 items-center flex-col w-full">
                      <h3>Λεπτομέρειες</h3>
                  </div>
                  <div className="flex gap-3 items-center flex-col w-full">
                    <div className='flex flex-col gap-3 w-full px-16'>
                      <div className='w-full bg-powdergrey rounded-xl flex flex-row justify-between items-center px-6 py-5'>
                        <p className='text-grenite text-sm font-bold '>Τύπος</p>
                        {/* <p className='text-grenite text-sm'>Τιμολόγιο αποζημιώσεων επιδοτήσεων</p> */}
                        <p className='text-grenite text-sm'>{selectedTxs?.loyaltyTransactionType === "Accrual" ? `Συλλογή` : selectedTxs?.loyaltyTransactionType === "Redemption" ? `Εξαργύρωση` : ""}</p>
                      </div>
                      <div className='w-full bg-powdergrey rounded-xl flex flex-row justify-between items-center px-6 py-5'>
                        <p className='text-grenite text-sm font-bold '>Ημερομηνία</p>
                        {/* <p className='text-grenite text-sm'>31/12/2025</p> */}
                        <p className='text-grenite text-sm'>{new Date(selectedTxs?.processedDate).toLocaleDateString('el-GR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}</p>
                      </div>
                      <div className='w-full bg-powdergrey rounded-xl flex flex-row justify-between items-center px-6 py-5'>
                        <p className='text-grenite text-sm font-bold '>Κωδικός</p>
                        <p className='text-grenite text-sm'>{selectedTxs?.loyaltyTransactionNumber}</p>
                      </div>
                      <div className='w-full bg-powdergrey rounded-xl flex flex-row justify-between items-center px-6 py-5'>
                        <p className='text-grenite text-sm font-bold '>Χιλιόμετρα εξαργύρωσης</p>
                        <p className='text-grenite text-sm'>{selectedTxs?.loyaltyTransactionType === "Accrual" ? `+` : selectedTxs?.loyaltyTransactionType === "Redemption" ? `-` : ""}{(selectedTxs?.awardPoints.toLocaleString("el-GR"))} χλμ</p>
                      </div>
                      <div className='w-full bg-powdergrey rounded-xl flex flex-row justify-between items-center px-6 py-5'>
                        <p className='text-grenite text-sm font-bold '>Χιλιόμετρα αναβάθμισης</p>
                        <p className='text-grenite text-sm'>{selectedTxs?.tierPoints > 0 ? "+" : ""}{(selectedTxs?.tierPoints)?.toLocaleString("el-GR")} χλμ</p>
                      </div>
                      <div className='w-full bg-powdergrey rounded-xl flex flex-row justify-between items-center px-6 py-5'>
                        <p className='text-grenite text-sm font-bold pr-3'>Περιγραφή</p>
                        {/* <p className='text-grenite text-sm'>{selectedTxs?.comments}</p> */}
                        <p className='text-grenite text-sm'>{selectedTxs?.loyaltyProduct}</p>
                      </div>
                    </div>
                  </div>
              </div>
            </CustomModal>
          </div>
        </div>
      </div>
    </div>
    
  );
};

export default LoyaltyPage;
